// src/l1packs/russian.js
// Source: "Norwegian speech sounds compared to Russian" (Olaf Husby, NTNU NoW)
//
// Normalizations:
// - Use canonical IPA /ɡ/ (PDF table writes /g/)
// - Use canonical IPA /oː/ (PDF table writes /o:/)
// - PDF sometimes uses "+~" (between + and ~); we map that to "~" because UI supports only +/~/-.
//
// Visualization tags vocabulary (consonants)
// place:* => lips | teeth | ridge | palate | velum
// air:*   => burst | noisy | smooth
// sec:*   => palatalized | velarized
// phon:*  => voiced | devoice-final
// hint:*  => less-aspirated-ru
//
// Added for UI fields:
// - userComment: detailed learner-facing explanation (articulatory + contrast)
// - easyComment: ultra-short “remember this” line (one breath)
// - difficulty: "ok" | "watch" | "new"   (new = “red” / category-creating for RU learners)
// - keywords: array of strings for UI + future search/filters + visualization hooks
//   Examples: "front-rounded", "central-rounded", "retroflex", "velar-nasal", "aspiration",
//             "length-phonemic", "palatal-fricative", "no-final-g"
//
// Added for vowels:
// - visVowel: ONLY when note is about vowel QUALITY (front/central/back, close/mid/open, rounding).
//             If the note is only about LENGTH (shorter/longer), keep visVowel undefined.

export const RUSSIAN_PACK = {
  meta: {
    code: "ru",
    name: "Russian",
    source: "NTNU NoW – Norwegian speech sounds compared to Russian (Olaf Husby)",
  },

  byIpa: {
    // ---- Consonants (p.2) ----

    p: {
      overlap: "+",
      no: ["park"],
      l1: ["парк"],
      comment: "Less aspirated in RU",
      easyComment: "Norwegian /p/ needs a stronger air burst than Russian.",
      userComment:
        "Bilabial stop. Lips close and release. Norwegian /p/ has a strong burst of air. If you use the Russian version (weak burst), Norwegians may hear it closer to /b/.",
      difficulty: "watch",
      keywords: ["aspiration", "air-burst", "may-sound-like-b"],
      vis: { tags: ["place:lips", "air:burst", "hint:less-aspirated-ru"] },
    },

    b: {
      overlap: "+",
      no: ["boks"],
      l1: ["бокс"],
      comment: "±palatalized, unvoiced in final position",
      easyComment: "RU /b/ can devoice at word end; keep voicing in NO when needed.",
      userComment:
        "Bilabial voiced stop. Lips close and vocal folds vibrate. In Russian it may be “soft” (palatalized), and at the end of a word it can lose voicing. Norwegian does not use palatalization as a main contrast, so avoid over-softening.",
      difficulty: "watch",
      keywords: ["voicing", "final-devoicing", "palatalization"],
      vis: { tags: ["place:lips", "phon:voiced", "sec:palatalized", "phon:devoice-final"] },
    },

    t: {
      overlap: "~",
      no: ["tema"],
      l1: ["тема"],
      comment: "±palatalized, less aspirated in RU",
      easyComment: "NO /t/ has more air than RU /t/; don’t make it too “soft”.",
      userComment:
        "Alveolar stop (ridge). Tongue touches the ridge behind the teeth. Norwegian /t/ typically has a stronger burst of air. If you use the Russian version, it can sound closer to /d/ in Norwegian ears. Russian /t/ can also be palatalized; Norwegian usually doesn’t want that “soft” coloring here.",
      difficulty: "watch",
      keywords: ["aspiration", "palatalization", "may-sound-like-d"],
      vis: { tags: ["place:ridge", "air:burst", "sec:palatalized", "hint:less-aspirated-ru"] },
    },

    d: {
      overlap: "~",
      no: ["doctor"],
      l1: ["доктор"],
      comment: "±palatalized, unvoiced in final position",
      easyComment: "Watch RU final devoicing and avoid over-palatalizing in NO.",
      userComment:
        "Alveolar voiced stop (ridge). Tongue touches the ridge and vocal folds vibrate. In Russian it may be palatalized and can lose voicing at the end of a word. Norwegian learners often need to keep voicing where it matters.",
      difficulty: "watch",
      keywords: ["voicing", "final-devoicing", "palatalization"],
      vis: { tags: ["place:ridge", "phon:voiced", "sec:palatalized", "phon:devoice-final"] },
    },

    // Norwegian retroflex series: new category for RU learners
    "ʈ": {
      overlap: "-",
      no: ["(rt) as in: *fort* (dialect/variety dependent)"],
      l1: [],
      comment: "Norwegian retroflex (often /r/ + /t/ merges)",
      easyComment: "In NO, /r + t/ can merge into one retroflex sound.",
      userComment:
        "Retroflex stop (tongue tip curls slightly back). Norwegian often merges /r/ + /t/ into a single sound. Do not pronounce two separate consonants; aim for one merged retroflex.",
      difficulty: "new",
      keywords: ["retroflex", "r-plus-consonant", "merge", "one-sound"],
      // current UI landmark might still show ridge; keep keyword for future precise landmark
      vis: { tags: ["place:ridge", "air:burst"] },
    },

    "ɖ": {
      overlap: "-",
      no: ["(rd) as in: *gård* (dialect/variety dependent)"],
      l1: [],
      comment: "Norwegian retroflex (often /r/ + /d/ merges)",
      easyComment: "In NO, /r + d/ can become one retroflex sound.",
      userComment:
        "Retroflex voiced stop. Norwegian often merges /r/ + /d/ into one sound. Russians typically keep a separate /r/; in Norwegian you often want a single merged consonant.",
      difficulty: "new",
      keywords: ["retroflex", "r-plus-consonant", "merge", "voicing"],
      vis: { tags: ["place:ridge", "phon:voiced", "air:burst"] },
    },

    k: {
      overlap: "~",
      no: ["kakao"],
      l1: ["какао"],
      comment: "±palatalized, less aspirated in RU",
      easyComment: "NO /k/ often needs more air than RU /k/.",
      userComment:
        "Velar stop. Back of tongue closes at the velum. Norwegian /k/ often has a stronger burst of air; a Russian-like weak burst can sound closer to /ɡ/. Russian also has palatalized (“soft”) versions; avoid adding that softness unless Norwegian context calls for it.",
      difficulty: "watch",
      keywords: ["aspiration", "palatalization", "may-sound-like-g"],
      vis: { tags: ["place:velum", "air:burst", "sec:palatalized", "hint:less-aspirated-ru"] },
    },

    "ɡ": {
      overlap: "~",
      no: ["granat"],
      l1: ["гранат"],
      comment: "±palatalized, unvoiced in final position",
      easyComment: "RU /ɡ/ can devoice at the end; don’t automatically do that in NO.",
      userComment:
        "Velar voiced stop. Back of tongue closes at the soft palate. In Russian it may be palatalized and can lose voicing in final position. Norwegian doesn’t want systematic final devoicing the same way, so keep voicing when it matters.",
      difficulty: "watch",
      keywords: ["voicing", "final-devoicing", "palatalization"],
      vis: { tags: ["place:velum", "phon:voiced", "sec:palatalized", "phon:devoice-final"] },
    },

    m: {
      overlap: "+",
      no: ["masse"],
      l1: ["масса"],
      comment: "",
      easyComment: "Same idea in RU and NO: lips closed, air through the nose.",
      userComment:
        "Bilabial nasal. Lips close but air flows through the nose, so airflow is smooth and continuous.",
      difficulty: "ok",
      keywords: ["nasal"],
      vis: { tags: ["place:lips", "air:smooth"] },
    },

    n: {
      overlap: "~",
      no: ["NATO"],
      l1: ["нато"],
      comment: "±palatalized",
      easyComment: "RU may use a “soft n”; NO usually wants a plain n here.",
      userComment:
        "Alveolar nasal (ridge). Tongue touches the ridge behind the teeth and air flows through the nose. Russian can have a palatalized (“soft”) version; Norwegian typically doesn’t contrast this here, so avoid over-softening.",
      difficulty: "watch",
      keywords: ["nasal", "palatalization"],
      vis: { tags: ["place:ridge", "air:smooth", "sec:palatalized"] },
    },

    "ɳ": {
      overlap: "-",
      no: ["(rn) as in: *barn* (dialect/variety dependent)"],
      l1: [],
      comment: "Norwegian retroflex nasal (often /r/ + /n/ merges)",
      easyComment: "NO /r + n/ can merge into one retroflex nasal.",
      userComment:
        "Retroflex nasal. Norwegian can merge /r/ + /n/ into a single retroflex nasal. Aim for one consonant, not an /r/ plus /n/ sequence.",
      difficulty: "new",
      keywords: ["retroflex", "nasal", "r-plus-consonant", "merge"],
      vis: { tags: ["place:ridge", "air:smooth"] },
    },

    // Velar nasal: new-ish for RU learners as a phoneme (esp. word-final)
    "ŋ": {
      overlap: "-",
      no: ["sang", "lang"],
      l1: [],
      comment: "No release [g] (don’t say 'ng')",
      easyComment: "NO /ŋ/ is one sound: no extra [g] at the end.",
      userComment:
        "Velar nasal. Back of the tongue contacts the soft palate while air flows through the nose. Do not release a [g] after it — avoid pronouncing ‘ng’ as [nɡ].",
      difficulty: "new",
      keywords: ["velar-nasal", "no-final-g", "one-sound"],
      vis: { tags: ["place:velum", "air:smooth"] },
    },

    f: {
      overlap: "+",
      no: ["kafé"],
      l1: ["кафе"],
      comment: "",
      easyComment: "Similar in RU and NO: lower lip + teeth, noisy air.",
      userComment:
        "Labiodental fricative. Lower lip approaches the teeth and creates noisy airflow.",
      difficulty: "ok",
      keywords: ["fricative"],
      vis: { tags: ["place:teeth", "air:noisy"] },
    },

    "ʋ": {
      overlap: "~",
      no: ["vampyr"],
      l1: ["вампир"],
      comment: "May be unvoiced in final position",
      easyComment: "NO /ʋ/ is smoother than RU /v/; watch final devoicing.",
      userComment:
        "Labiodental approximant. Lip approaches the teeth with smooth airflow (less friction than Russian /v/). It may lose voicing at the end of a word.",
      difficulty: "watch",
      keywords: ["approximant", "not-fricative", "final-devoicing"],
      vis: { tags: ["place:teeth", "air:smooth", "phon:devoice-final"] },
    },

    s: {
      overlap: "+",
      no: ["samovar"],
      l1: ["самовар"],
      comment: "",
      easyComment: "Same core sound: narrow gap at the ridge, noisy air.",
      userComment:
        "Alveolar fricative. Tongue near the ridge makes a narrow gap and noisy airflow.",
      difficulty: "ok",
      keywords: ["fricative"],
      vis: { tags: ["place:ridge", "air:noisy"] },
    },

    "ʃ": {
      overlap: "+",
      no: ["sjokolade"],
      l1: ["шоколад"],
      comment: "",
      easyComment: "Like RU /ш/: noisier, farther back than /s/.",
      userComment:
        "Postalveolar-ish fricative. Similar to Russian /ш/. For now shown as ridge with noisy airflow (a more back landmark can be added later).",
      difficulty: "ok",
      keywords: ["fricative", "postalveolar-ish"],
      vis: { tags: ["place:ridge", "air:noisy"] },
    },

    "ç": {
      overlap: "~",
      no: ["kirurg"],
      l1: ["хирург"],
      comment: "Letter <x> before <и>",
      easyComment: "Smile + raise the middle of the tongue: palatal fricative.",
      userComment:
        "Palatal fricative. Middle of the tongue lifts toward the hard palate and creates noisy airflow. Russian speakers often replace this with [x]; keep it more forward and ‘smiley’.",
      difficulty: "new",
      keywords: ["palatal-fricative", "front", "smile-cue"],
      vis: { tags: ["place:palate", "air:noisy"] },
    },

    j: {
      overlap: "+",
      no: ["jogg"],
      l1: ["йог"],
      comment: "",
      easyComment: "Same idea: a smooth glide toward the palate.",
      userComment:
        "Palatal glide. Tongue body raised toward the hard palate with smooth airflow.",
      difficulty: "ok",
      keywords: ["glide"],
      vis: { tags: ["place:palate", "air:smooth"] },
    },

    h: {
      overlap: "~",
      no: ["hockey"],
      l1: ["хоккей"],
      comment: "Before front vowel ~ [ç], back vowel ~ [x]",
      easyComment: "NO /h/ shifts: front vowels → [ç], back vowels → [x].",
      userComment:
        "This fricative shifts place: before front vowels it is more palatal; before back vowels it is more velar. Russian speakers often use a strong [x] everywhere or drop /h/—aim for the appropriate lighter target.",
      difficulty: "watch",
      keywords: ["context-dependent", "palatal-vs-velar", "avoid-x-everywhere"],
      vis: { tags: ["place:velum", "air:noisy", "sec:palatalized"] },
    },

    r: {
      overlap: "+",
      no: ["ris"],
      l1: ["рис"],
      comment: "",
      easyComment: "Norwegian /r/ may be lighter than Russian.",
      userComment:
        "R sound. Russian often uses a strong trill. Norwegian may be a lighter tap/trill (and varies by dialect), so don’t over-trill unless that’s the target variety.",
      difficulty: "watch",
      keywords: ["r-variation", "tap-vs-trill"],
      vis: { tags: ["place:ridge", "air:smooth"] },
    },

    l: {
      overlap: "~",
      no: ["metall"],
      l1: ["металл"],
      comment: "May be velarized, ±palatalized",
      easyComment: "RU often uses darker /l/; NO is often lighter unless ‘dark L’ is intended.",
      userComment:
        "Lateral. Air flows along the sides of the tongue. Russian often has a darker (velarized) /l/ as a default; Norwegian is often lighter unless explicitly velarized. Avoid making all Norwegian /l/ sound dark.",
      difficulty: "watch",
      keywords: ["l-darkness", "velarized", "palatalization"],
      vis: { tags: ["place:ridge", "air:smooth", "sec:velarized", "sec:palatalized"] },
    },

    "ɭ": {
      overlap: "-",
      no: ["(rl) as in: *perl* (dialect/variety dependent)"],
      l1: [],
      comment: "Norwegian retroflex lateral (often /r/ + /l/ merges)",
      easyComment: "NO /r + l/ can merge into one retroflex ‘L-like’ sound.",
      userComment:
        "Retroflex lateral. Norwegian can merge /r/ + /l/ into a single retroflex lateral. Don’t pronounce a separate /r/ first; aim for one merged consonant.",
      difficulty: "new",
      keywords: ["retroflex", "lateral", "r-plus-consonant", "merge"],
      vis: { tags: ["place:ridge", "air:smooth"] },
    },

    // ---- Vowels / diphthongs ----
    // IMPORTANT:
    // - If note is ONLY length (“shorter/longer”), keep visVowel undefined.
    // - If note is quality (“more central/front/back/open”, rounding), add visVowel.

    i: {
      overlap: "+",
      no: ["risk"],
      l1: ["риск"],
      comment: "",
      easyComment: "Pretty similar in RU and NO.",
      userComment: "Close front vowel. Russian and Norwegian are very similar here.",
      difficulty: "ok",
      keywords: [],
    },

    "iː": {
      overlap: "~",
      no: ["ris"],
      l1: ["рис"],
      comment: "RU vowel is shorter than NO",
      easyComment: "In NO, long vs short matters; RU is often shorter.",
      userComment:
        "Vowel quality is similar, but Russian is typically shorter. In Norwegian, length is part of the vowel identity and can change meaning.",
      difficulty: "watch",
      keywords: ["length-phonemic"],
    },

    // Front rounded vowels: NEW category for RU learners
    y: {
      overlap: "-",
      no: ["lyd", "syk"],
      l1: [],
      comment: "Front rounded vowel (no direct RU equivalent)",
      easyComment: "Tongue like /i/ + lips like /u/ (front + rounded).",
      userComment:
        "This vowel does not exist in Russian. Start from /i/ (tongue forward, ‘smile’ position), then round the lips as for /u/. Keep the tongue forward while the lips are rounded.",
      difficulty: "new",
      keywords: ["front-rounded", "new-vowel", "i-plus-u-lips"],
      // future-ready visualization hook (today you may still only have generic vowel chart)
      visVowel: {
        // optional placeholder curves; tweak later when you add a better vowel map
        no: {
          curve: {
            start: { x: 0.35, y: 0.20 },
            mid: { x: 0.35, y: 0.20 },
            end: { x: 0.35, y: 0.20 },
          },
        },
      },
    },

    "yː": {
      overlap: "-",
      no: ["ny", "dyr"],
      l1: [],
      comment: "Long front rounded vowel (no direct RU equivalent)",
      easyComment: "Same as /y/, but hold it longer (NO length contrast).",
      userComment:
        "Same tongue/lip target as /y/ (front + rounded), but longer. Norwegian uses length as part of the vowel category, so sustaining it matters.",
      difficulty: "new",
      keywords: ["front-rounded", "new-vowel", "length-phonemic"],
      visVowel: {
        no: {
          curve: {
            start: { x: 0.35, y: 0.20 },
            mid: { x: 0.35, y: 0.20 },
            end: { x: 0.35, y: 0.20 },
          },
        },
      },
    },

    e: {
      overlap: "~",
      no: ["ekko"],
      l1: ["эхо"],
      comment: "RU vowel is more central: [ə]",
      easyComment: "NO /e/ is more front; don’t relax it into RU-like central [ə].",
      userComment:
        "Norwegian /e/ is more front. The closest Russian vowel is often more central (closer to ‘uh’). Keep the lips slightly spread and the tongue more forward—avoid relaxing into a central vowel.",
      difficulty: "watch",
      keywords: ["front-vs-central"],
      visVowel: {
        no: {
          curve: {
            start: { x: 0.43, y: 0.40 },
            mid: { x: 0.5, y: 0.25 },
            end: { x: 0.73, y: 0.4 },
          },
        },
        l1: {
          curve: {
            start: { x: 0.43, y: 0.40 },
            mid: { x: 0.63, y: 0.24 },
            end: { x: 0.74, y: 0.41 },
          },
        },
      },
    },

    "eː": {
      overlap: "~",
      no: ["tema"],
      l1: ["тема"],
      comment: "RU vowel is shorter than NO",
      easyComment: "NO length matters; hold it longer than RU.",
      userComment:
        "Vowel quality is similar, but Russian is typically shorter. In Norwegian, long vs short can change meaning—sustain the vowel.",
      difficulty: "watch",
      keywords: ["length-phonemic"],
    },

    // More front rounded vowels: NEW for RU learners
    "ø": {
      overlap: "-",
      no: ["sønn", "høst"],
      l1: [],
      comment: "Front rounded mid vowel (no direct RU equivalent)",
      easyComment: "Tongue like /e/ + lips rounded (as for /u/).",
      userComment:
        "This vowel does not exist in Russian. Start from /e/ (mid-front), then round the lips while keeping the tongue forward. Don’t let the tongue drift back toward /o/.",
      difficulty: "new",
      keywords: ["front-rounded", "new-vowel", "e-plus-rounded-lips"],
      visVowel: {
        no: {
          curve: {
            start: { x: 0.42, y: 0.35 },
            mid: { x: 0.42, y: 0.35 },
            end: { x: 0.42, y: 0.35 },
          },
        },
      },
    },

    "øː": {
      overlap: "-",
      no: ["høre", "søt"],
      l1: [],
      comment: "Long front rounded mid vowel (no direct RU equivalent)",
      easyComment: "Same as /ø/, but longer (NO length contrast).",
      userComment:
        "Same tongue/lip target as /ø/ (front + rounded), but longer. Norwegian length is meaningful, so sustain it.",
      difficulty: "new",
      keywords: ["front-rounded", "new-vowel", "length-phonemic"],
      visVowel: {
        no: {
          curve: {
            start: { x: 0.42, y: 0.35 },
            mid: { x: 0.42, y: 0.35 },
            end: { x: 0.42, y: 0.35 },
          },
        },
      },
    },

    // Norwegian /æ/ is also category-creating for many RU learners (often merged toward /e/ or /a/)
    "æ": {
      overlap: "-",
      no: ["hær", "bære"],
      l1: [],
      comment: "Open front vowel (no single RU match)",
      easyComment: "More open than /e/ (jaw drops), but tongue stays forward.",
      userComment:
        "Open front vowel. Russian speakers often drift toward /e/ (too close) or /a/ (too back). Keep the tongue forward like /e/, but drop the jaw more.",
      difficulty: "new",
      keywords: ["new-vowel", "open-front", "jaw-drop"],
      visVowel: {
        no: {
          curve: {
            start: { x: 0.32, y: 0.62 },
            mid: { x: 0.32, y: 0.62 },
            end: { x: 0.32, y: 0.62 },
          },
        },
      },
    },

    "æː": {
      overlap: "-",
      no: ["være", "lære"],
      l1: [],
      comment: "Long open front vowel",
      easyComment: "Same as /æ/, but longer (NO length contrast).",
      userComment:
        "Same target as /æ/ (open-front), but longer. Sustain it—Norwegian length is part of the vowel category.",
      difficulty: "new",
      keywords: ["new-vowel", "open-front", "length-phonemic"],
      visVowel: {
        no: {
          curve: {
            start: { x: 0.32, y: 0.62 },
            mid: { x: 0.32, y: 0.62 },
            end: { x: 0.32, y: 0.62 },
          },
        },
      },
    },

    "ɑ": {
      overlap: "+",
      no: ["alfabet"],
      l1: ["алфавит"],
      comment: "RU vowel is more front than NO",
      easyComment: "NO /ɑ/ is farther back; think ‘open /o/’ feeling.",
      userComment:
        "Open vowel. Norwegian /ɑ/ is farther back. The closest Russian vowel is more front. Try: start from /o/ in tongue position and open the jaw—keep it back.",
      difficulty: "watch",
      keywords: ["backness"],
      visVowel: {
        l1: {
          curve: {
            start: { x: 0.43, y: 0.40 },
            mid: { x: 0.5, y: 0.25 },
            end: { x: 0.73, y: 0.4 },
          },
        },
        no: {
          curve: {
            start: { x: 0.43, y: 0.40 },
            mid: { x: 0.63, y: 0.24 },
            end: { x: 0.74, y: 0.41 },
          },
        },
      },
    },

    "ɑː": {
      overlap: "~",
      no: ["da"],
      l1: ["да"],
      comment: "RU vowel is more front than NO",
      easyComment: "Same back /ɑ/ target, but hold it longer in NO.",
      userComment:
        "Similar to /ɑ/, but longer. The closest Russian vowel is still more front than Norwegian, and Norwegian length is meaningful—sustain it.",
      difficulty: "watch",
      keywords: ["backness", "length-phonemic"],
      visVowel: {
        l1: {
          curve: {
            start: { x: 0.43, y: 0.40 },
            mid: { x: 0.5, y: 0.25 },
            end: { x: 0.73, y: 0.4 },
          },
        },
        no: {
          curve: {
            start: { x: 0.43, y: 0.40 },
            mid: { x: 0.63, y: 0.24 },
            end: { x: 0.74, y: 0.41 },
          },
        },
      },
    },

    // Central rounded vowel: NEW category for RU learners
    "ʉ": {
      overlap: "-",
      no: ["hus", "full"],
      l1: [],
      comment: "Central rounded vowel (no direct RU equivalent)",
      easyComment: "Start from /u/, move tongue slightly forward—keep lips rounded.",
      userComment:
        "Central rounded vowel. This category does not exist in Russian. Say /u/ (rounded lips), then move the tongue slightly forward toward the center without changing lip rounding.",
      difficulty: "new",
      keywords: ["central-rounded", "new-vowel", "u-but-forward"],
      visVowel: {
        no: {
          curve: {
            start: { x: 0.55, y: 0.25 },
            mid: { x: 0.55, y: 0.25 },
            end: { x: 0.55, y: 0.25 },
          },
        },
      },
    },

    "ʉː": {
      overlap: "-",
      no: ["brun", "hus"],
      l1: [],
      comment: "Long central rounded vowel",
      easyComment: "Same /ʉ/ target, but longer in NO.",
      userComment:
        "Same tongue/lip target as /ʉ/ (central + rounded), but longer. Sustain it—Norwegian length matters.",
      difficulty: "new",
      keywords: ["central-rounded", "new-vowel", "length-phonemic"],
      visVowel: {
        no: {
          curve: {
            start: { x: 0.55, y: 0.25 },
            mid: { x: 0.55, y: 0.25 },
            end: { x: 0.55, y: 0.25 },
          },
        },
      },
    },

    u: {
      overlap: "+",
      no: ["punkt"],
      l1: ["пункт"],
      comment: "",
      easyComment: "Fairly similar in RU and NO.",
      userComment: "Back close rounded vowel. Russian and Norwegian are fairly similar here.",
      difficulty: "ok",
      keywords: [],
    },

    "uː": {
      overlap: "~",
      no: ["ord"],
      l1: ["ур"],
      comment: "RU vowel is shorter than NO",
      easyComment: "Hold it longer in NO; length can change meaning.",
      userComment:
        "Vowel quality is similar, but Russian is typically shorter. Norwegian length is meaningful, so sustain the vowel.",
      difficulty: "watch",
      keywords: ["length-phonemic"],
    },

    o: {
      overlap: "+",
      no: ["koks"],
      l1: ["кокс"],
      comment: "",
      easyComment: "Similar in both languages.",
      userComment: "Back mid rounded vowel. Similar in both languages.",
      difficulty: "ok",
      keywords: [],
    },

    "oː": {
      overlap: "~",
      no: ["på"],
      l1: ["по"],
      comment: "RU vowel is shorter than NO",
      easyComment: "Norwegian long /oː/ is longer than Russian.",
      userComment:
        "Vowel quality is similar, but Russian is typically shorter. In Norwegian, length is part of the vowel identity—hold it.",
      difficulty: "watch",
      keywords: ["length-phonemic"],
    },

    // Diphthongs:
    // (kept without visVowel for now; keywords prepared for future overlays)
    "ɑi": {
      overlap: "~",
      no: ["mai"],
      l1: ["май"],
      comment: "NO /ɑ/ is more back",
      easyComment: "NO starts this diphthong farther back.",
      userComment:
        "Norwegian starts the diphthong with a more back /ɑ/. Russian starts a bit more forward.",
      difficulty: "watch",
      keywords: ["diphthong", "back-start"],
    },

    "æi": {
      overlap: "~",
      no: ["ei"],
      l1: ["эй"],
      comment: "NO starts more open: /æ/",
      easyComment: "NO starts more open (jaw lower) than the RU-like version.",
      userComment:
        "Norwegian starts more open (jaw more open) than the closest Russian version.",
      difficulty: "watch",
      keywords: ["diphthong", "open-start"],
    },

    "øy": {
      overlap: "-",
      no: ["høy", "øy"],
      l1: [],
      comment: "Diphthong with front rounding (hard for RU)",
      easyComment: "Front + rounded movement: expect this to be new.",
      userComment:
        "This diphthong involves front rounded targets that Russian does not have. Learn the front rounded vowel feeling (like /y/ or /ø/) and then practice the glide.",
      difficulty: "new",
      keywords: ["diphthong", "front-rounded", "new-category"],
    },

    "æʉ": {
      overlap: "-",
      no: ["au? (dialect/word dependent)"],
      l1: [],
      comment: "Diphthong involving central rounding",
      easyComment: "Includes /ʉ/-like central rounding (new for RU).",
      userComment:
        "This diphthong involves a central rounded target (/ʉ/-like), which is a new category for Russian speakers. First learn /ʉ/, then add the glide.",
      difficulty: "new",
      keywords: ["diphthong", "central-rounded", "new-category"],
    },

    oy: {
      overlap: "+",
      no: ["Roy"],
      l1: ["рой"],
      comment: "RU /o/ is longer than NO",
      easyComment: "Main difference is timing/length.",
      userComment: "Main difference is length: Russian /o/ tends to be longer here.",
      difficulty: "watch",
      keywords: ["length"],
    },

    // NOTE: RU example is vulgar; keep for fidelity OR mask it in the UI layer.
    uy: {
      overlap: "+",
      no: ["hoi"],
      l1: ["хуй"],
      comment: "Rare in NO (NB: RU word is vulgar)",
      easyComment: "UI note: RU example is vulgar; consider masking.",
      userComment:
        "Rare in Norwegian. (UI note: the Russian example is vulgar; consider masking or replacing at render time.)",
      difficulty: "watch",
      keywords: ["ui-vulgar", "rare"],
    },

    "ʉy": {
      overlap: "-",
      no: [],
      l1: [],
      comment: "Rare in NO",
      easyComment: "Rare; involves /ʉ/ (central rounded) + /y/ (front rounded).",
      userComment:
        "Rare in Norwegian. If you meet it, it combines two categories that are new for Russian speakers: /ʉ/ (central rounded) and /y/ (front rounded).",
      difficulty: "new",
      keywords: ["rare", "central-rounded", "front-rounded", "new-category"],
    },
  },
};
